<template>
  <component
    :is="to ? 'AppLink' : 'div'"
    :to="to || undefined"
  >
    <div
      v-if="is_selected"
      :class="class_resolved"
      :style="style"
    >
      <AppIcon name="solid-check" />
    </div>
    <img
      v-else-if="show_image"
      alt="avatar"
      :src="url"
      :style="style"
      @error="error = true"
    />
    <div
      v-else
      :class="class_avatar"
      :style="style"
    >
      <AppIcon
        v-if="icon_name"
        :name="icon_name"
        size="lg"
      />
      <span v-else>{{ user_initial }}</span>
    </div>
  </component>
</template>

<script>
import { Tenant } from '@/app/data/model_constants';

const state_to_icon = Object.freeze({
  inbound_email: 'solid-envelope',
  inbound_point: 'regular-house-chimney-heart',
  removed_user: 'solid-user',
});
</script>

<script setup>
import { computed, inject, ref, watch } from 'vue';
import INJECT_KEYS from '@/app/data/inject_keys';
import avatarColor from '@/app/util/avatar_color';

const props = defineProps({
  cache: { type: Boolean, default: true },
  username: { type: String, default: '' },
  src: { type: String, default: null },
  size: { type: String, default: null },
  format: {
    type: String,
    default: Tenant.LOGO_FORMAT_ROUND,
  },
  is_selected: { type: Boolean },
  state: {
    type: String,
    default: null,
    validator: (prop) => Object.keys(state_to_icon).includes(prop),
  },
  to: { type: Object, default: null },
  // Styles the avatar yourself, opting out of the name-derived color below.
  class_props: { type: String, default: null },
});

const CLASS_FALLBACK = 'has-background-primary has-text-primary-light';

const datatable_size = inject(INJECT_KEYS.DATATABLE_SIZE, null);

const error = ref(false);
const icon_name = state_to_icon[props.state];

watch(
  () => props.src,
  () => {
    error.value = false;
  },
);

const size_resolved = computed(() => {
  if (props.size) return props.size;
  const dt_size = datatable_size;
  const size_val = dt_size && dt_size.value !== undefined ? dt_size.value : dt_size;
  if (size_val === 'small') return '24';
  if (size_val === 'large') return '32';
  return '28';
});

const show_image = computed(() => !!props.src && !error.value);

const user_initial = computed(() => {
  if (props.username.length > 0) {
    const parts = props.username.split(/[ -]/);
    let initials = '';
    parts.forEach((part) => {
      initials += part.charAt(0);
    });
    if (initials.length > 3 && initials.search(/[A-Z]/) !== -1) {
      initials = initials.replace(/[a-z]+/g, '');
    }
    if (initials[2] === '&') {
      // in case of multiple customers (> 2) we just strip first 2 customers' initials
      return `${initials[0]}&${initials[1]}`;
    }
    return initials.substr(0, 3).toUpperCase();
  }
  return '';
});

const class_resolved = computed(() => props.class_props || CLASS_FALLBACK);

// Initials get a color derived from the name, so a person reads as the same color everywhere (see
// design_system/components/AppAvatar.vue). A status icon or an explicit class_props keeps the old
// primary fill: those say something about the message, not about who sent it.
const use_name_color = computed(() => !props.class_props && !props.is_selected && !icon_name);

const class_avatar = computed(() => (use_name_color.value ? undefined : class_resolved.value));

const style = computed(() => {
  const size = size_resolved.value;
  const style_new = {
    height: `${size}px`,
    width: `${size}px`,
    display: 'flex',
    borderRadius: '50%',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    objectFit: 'cover',
    fontSize: `${Math.floor(size / 2.5)}px`,
    fontWeight: 'var(--font-weight-semibold)',
    lineHeight: 1,
  };
  if (use_name_color.value) {
    const { background, color } = avatarColor(props.username);
    style_new.backgroundColor = background;
    style_new.color = color;
  }
  if (show_image.value) {
    style_new.backgroundColor = 'white';
  }
  if (props.format === Tenant.LOGO_FORMAT_SQUARE) {
    style_new.borderRadius = '6px';
  }
  if (props.is_selected) {
    style_new.height = `${Math.floor(size) + 2}px`;
    style_new.width = `${Math.floor(size) + 2}px`;
  }
  return style_new;
});

const url = computed(() => {
  if (_.startsWith(props.src, 'blob')) return props.src;
  if (props.cache) return props.src;

  const url_new = new URL(props.src);
  const bust = Math.round(new Date().getTime() / 1000);
  url_new.searchParams.append('_t', bust);

  return url_new.href;
});
</script>
